import { useEffect, useState, type FormEvent } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  ChevronUp,
  ChevronDown,
} from 'lucide-react'
import type { CreateOptionSetPayload, OptionSet, OptionSetOption } from '@/api/optionSetsApi'
import { useOptionSets, useOptionSetUsage } from '@/hooks/useOptionSets'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const TAKE = 25

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

// ─── Settings tab content ─────────────────────────────────────────────────────

// Per-user column layout. Name and the actions cell are locked — see
// src/components/table/index.ts for the pattern.
const OPTION_SETS_TABLE_ID = 'option-sets'

// `sortable` columns sort SERVER-side (z8nrz7e4h6): the list is fetched a page
// at a time, so the ids here are the backend's allowed sort keys
// (OPTION_SET_SORTS in option-sets.service). Options is the length of a JSON
// array, which the database cannot order by.
const OPTION_SETS_COLUMNS: TableColumnDef[] = [
  { id: 'name', header: 'Name', locked: true, defaultWidth: 240, sortable: true },
  { id: 'options', header: 'Options', defaultWidth: 120 },
  { id: 'lastUpdated', header: 'Last updated', defaultWidth: 160, sortable: true },
  // 2 icon buttons at 40px + 4px gap + px-4 padding.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 120 },
]

export function OptionSetsSettings() {
  const cols = useTableColumns(OPTION_SETS_TABLE_ID, OPTION_SETS_COLUMNS)
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.optionSetsManage)

  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [skip, setSkip] = useState(0)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q)
      setSkip(0)
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  const { list, create, update, remove } = useOptionSets({
    q: debouncedQ || undefined,
    skip,
    take: TAKE,
    sort,
  })

  const items = list.data?.items ?? []

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<OptionSet | null>(null)
  const [deleting, setDeleting] = useState<OptionSet | null>(null)

  const [createServerError, setCreateServerError] = useState<string | null>(null)
  const [editServerError, setEditServerError] = useState<string | null>(null)
  const [deleteServerError, setDeleteServerError] = useState<string | null>(null)

  if (!canManage) {
    return (
      <div className='flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
        <AlertCircle className='h-4 w-4 shrink-0' />
        You do not have permission to manage option sets.
      </div>
    )
  }

  function handleCreate(payload: CreateOptionSetPayload) {
    setCreateServerError(null)
    create.mutate(payload, {
      onSuccess: () => setCreateOpen(false),
      onError: e =>
        setCreateServerError(e instanceof Error ? e.message : 'Could not create option set.'),
    })
  }

  function handleUpdate(id: string, payload: CreateOptionSetPayload) {
    setEditServerError(null)
    update.mutate(
      { id, payload },
      {
        onSuccess: () => setEditing(null),
        onError: e =>
          setEditServerError(e instanceof Error ? e.message : 'Could not update option set.'),
      },
    )
  }

  function handleDelete(id: string) {
    setDeleteServerError(null)
    remove.mutate(id, {
      onSuccess: () => setDeleting(null),
      onError: e =>
        setDeleteServerError(e instanceof Error ? e.message : 'Could not delete option set.'),
    })
  }

  return (
    <div className='space-y-4'>
      {/* Toolbar */}
      <div className='flex items-center justify-between gap-3'>
        <Input
          placeholder='Search option sets…'
          value={q}
          onChange={e => setQ(e.target.value)}
          className='max-w-sm'
        />
        <div className='flex items-center gap-2'>
        <Button
          onClick={() => {
            setCreateServerError(null)
            setCreateOpen(true)
          }}
        >
          <Plus className='mr-2 h-4 w-4' />
          Create Option Set
        </Button>
        </div>
      </div>

      <Card>
        <CardContent className='p-0'>
          {list.isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {list.isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load option sets. {(list.error as Error).message}
            </div>
          )}

          {list.data && items.length === 0 && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              {debouncedQ || skip > 0
                ? 'No option sets match your search.'
                : 'No option sets yet. Create one to get started.'}
            </div>
          )}

          {list.data && items.length > 0 && (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={def.id === 'actions' ? 'text-right' : undefined}
                      resizable={def.id !== 'actions'}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(set => (
                  <TableRow key={set.id}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        name: <TableCell className='font-medium'>{set.name}</TableCell>,
                        options: (
                          <TableCell className='text-muted-foreground'>
                            {set.options.length}
                          </TableCell>
                        ),
                        lastUpdated: (
                          <TableCell className='text-muted-foreground'>
                            {formatDate(set.updatedAt)}
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            <div className='flex items-center justify-end gap-1'>
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Edit option set'
                                onClick={() => {
                                  setEditServerError(null)
                                  setEditing(set)
                                }}
                              >
                                <Pencil className='h-4 w-4' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Delete option set'
                                onClick={() => {
                                  setDeleteServerError(null)
                                  setDeleting(set)
                                }}
                              >
                                <Trash2 className='h-4 w-4 text-destructive' />
                              </Button>
                            </div>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <OptionSetFormDialog
          pending={create.isPending}
          serverError={createServerError}
          onClose={() => {
            setCreateServerError(null)
            setCreateOpen(false)
          }}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <OptionSetFormDialog
          set={editing}
          pending={update.isPending}
          serverError={editServerError}
          onClose={() => {
            setEditServerError(null)
            setEditing(null)
          }}
          onSubmit={payload => handleUpdate(editing.id, payload)}
        />
      )}

      {deleting && (
        <DeleteOptionSetDialog
          set={deleting}
          pending={remove.isPending}
          serverError={deleteServerError}
          onClose={() => {
            setDeleteServerError(null)
            setDeleting(null)
          }}
          onConfirm={() => handleDelete(deleting.id)}
        />
      )}
    </div>
  )
}

// ─── Create / Edit dialog ─────────────────────────────────────────────────────

interface OptionDraft {
  id: string
  label: string
}

function OptionSetFormDialog({
  set,
  pending,
  serverError,
  onClose,
  onSubmit,
}: {
  set?: OptionSet
  pending: boolean
  serverError?: string | null
  onClose: () => void
  onSubmit: (payload: CreateOptionSetPayload) => void
}) {
  const [name, setName] = useState(set?.name ?? '')
  const [description, setDescription] = useState(set?.description ?? '')
  const [options, setOptions] = useState<OptionDraft[]>(() =>
    set && set.options.length > 0
      ? set.options.map(o => ({ id: o.id, label: o.label }))
      : [{ id: crypto.randomUUID(), label: '' }],
  )
  const [formError, setFormError] = useState<string | null>(null)

  function move(index: number, dir: -1 | 1) {
    const target = index + dir
    if (target < 0 || target >= options.length) return
    const next = [...options]
    ;[next[index], next[target]] = [next[target], next[index]]
    setOptions(next)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setFormError('Name is required.')
      return
    }
    const cleaned: OptionSetOption[] = options
      .map(o => ({ id: o.id, label: o.label.trim() }))
      .filter(o => o.label.length > 0)
    if (cleaned.length === 0) {
      setFormError('Add at least one non-empty option.')
      return
    }
    setFormError(null)
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      options: cleaned,
    })
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{set ? 'Edit option set' : 'New option set'}</DialogTitle>
          <DialogDescription>
            A reusable, named set of dropdown options. Reference it from form fields in the builder.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='option-set-name'>Name</Label>
            <Input
              id='option-set-name'
              value={name}
              autoFocus
              onChange={e => setName(e.target.value)}
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='option-set-description'>Description</Label>
            <Textarea
              id='option-set-description'
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              className='resize-none'
            />
          </div>

          <div className='flex flex-col gap-2'>
            <Label>Options</Label>
            {options.map((opt, i) => (
              <div key={opt.id} className='flex items-center gap-1.5'>
                <Input
                  value={opt.label}
                  placeholder={`Option ${i + 1}`}
                  onChange={e => {
                    const next = [...options]
                    next[i] = { ...opt, label: e.target.value }
                    setOptions(next)
                  }}
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='shrink-0'
                  disabled={i === 0}
                  aria-label='Move option up'
                  onClick={() => move(i, -1)}
                >
                  <ChevronUp className='h-4 w-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='shrink-0'
                  disabled={i === options.length - 1}
                  aria-label='Move option down'
                  onClick={() => move(i, 1)}
                >
                  <ChevronDown className='h-4 w-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='shrink-0'
                  disabled={options.length <= 1}
                  aria-label='Remove option'
                  onClick={() => setOptions(options.filter((_, j) => j !== i))}
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>
            ))}
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='self-start'
              onClick={() => setOptions([...options, { id: crypto.randomUUID(), label: '' }])}
            >
              <Plus className='mr-1.5 h-3.5 w-3.5' /> Add option
            </Button>
          </div>

          {formError && <p className='text-sm text-destructive'>{formError}</p>}
          {serverError && <p className='text-sm text-destructive'>{serverError}</p>}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !name.trim()}>
              {pending ? 'Saving…' : set ? 'Save changes' : 'Create option set'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Delete dialog ────────────────────────────────────────────────────────────

function DeleteOptionSetDialog({
  set,
  pending,
  serverError,
  onClose,
  onConfirm,
}: {
  set: OptionSet
  pending: boolean
  serverError?: string | null
  onClose: () => void
  onConfirm: () => void
}) {
  const usage = useOptionSetUsage(set.id)
  const referenced = !!usage.data && usage.data.count > 0

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Delete "{set.name}"?</DialogTitle>
          <DialogDescription>
            This removes the option set. It cannot be deleted while forms still reference it.
          </DialogDescription>
        </DialogHeader>

        {usage.isLoading && (
          <p className='text-xs text-muted-foreground'>Checking form usage…</p>
        )}
        {usage.isError && (
          <p className='text-xs text-muted-foreground'>Could not load form usage.</p>
        )}
        {usage.data && usage.data.count === 0 && (
          <p className='text-xs text-muted-foreground'>Not referenced by any form.</p>
        )}
        {referenced && (
          <div className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2'>
            <p className='text-xs font-medium text-amber-800'>
              Referenced by {usage.data!.count} form{usage.data!.count === 1 ? '' : 's'} — cannot
              delete.
            </p>
            <ul className='mt-1 space-y-0.5'>
              {usage.data!.forms.map(f => (
                <li key={f.id} className='text-xs text-amber-700'>
                  {f.name} <span className='opacity-70'>v{f.version}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {serverError && <p className='text-sm text-destructive'>{serverError}</p>}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            type='button'
            variant='destructive'
            onClick={onConfirm}
            disabled={pending || referenced || usage.isLoading}
          >
            {pending ? 'Deleting…' : 'Delete option set'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
